import { ContactList } from "components/ui/ContactList";
import { Landmark } from "lucide-react";
import { useBoardMembers } from "context/BoardMembersContext";
import { useEffect, useMemo } from "react";
import { useAuth } from "context/AuthContext";
import { formatTimestamp } from "utils/dateFormat";
import { contactListItemClasses } from "utils/contactListItemClasses";
import { boardMemberListSubtitle } from "utils/contactListSubtitle";
import { ReplyMuteBadge } from "components/ReplyMuteBadge";
import type { ReplyMute } from "types/ReplyMute";

interface BoardMembersListProps {
  onSelectContact: (contactId: number) => void;
  selectedContactId: number | null;
  condoId: number;
}

type BoardMemberRow = {
  id: number;
  contactId: number;
  name: string;
  subtitle: string;
  lastMessageAt?: string | null;
  hasUnread?: boolean;
  isActive: boolean;
  replyMute?: ReplyMute | null;
};

export function BoardMembersList({
  onSelectContact,
  selectedContactId,
  condoId,
}: BoardMembersListProps) {
  const { boardMembers, loading, error, refresh, markContactAsRead } =
    useBoardMembers();
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";

  const handleSelectContact = (contactId: number) => {
    markContactAsRead(contactId);
    onSelectContact(contactId);
  };

  useEffect(() => {
    refresh(condoId);
  }, [condoId, refresh]);

  const listItems: BoardMemberRow[] = useMemo(
    () =>
      boardMembers.map((member) => ({
        id: member.id,
        contactId: member.contact.id,
        name: member.contact.name,
        subtitle: boardMemberListSubtitle(member, locale),
        lastMessageAt: member.lastMessageAt,
        hasUnread: member.hasUnread,
        isActive: member.isActive ?? true,
        replyMute: member.replyMute ?? null,
      })),
    [boardMembers, locale],
  );

  if (!error && listItems.length === 0) {
    return null;
  }

  return (
    <ContactList<BoardMemberRow>
      listId="board-members"
      title={locale === "es" ? "Comisión" : "Board members"}
      icon={<Landmark className="w-4 h-4 mr-2" />}
      items={listItems}
      loading={loading}
      error={error}
      searchPlaceholder={
        locale === "es" ? "Buscar comisión..." : "Search board members..."
      }
      selectedId={selectedContactId}
      onSelectItem={handleSelectContact}
      getItemName={(item) => `${item.name} ${item.subtitle}`.trim()}
      getSelectionId={(item) => item.contactId}
      countItemsForHeader={(item) => item.isActive}
      renderItem={(item, isSelected) => (
        <div
          className={contactListItemClasses(isSelected, {
            inactive: !item.isActive,
          })}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div
                className={`truncate text-xs lg:text-sm ${
                  item.hasUnread ? "font-bold" : "font-medium"
                } ${!item.isActive ? "text-gray-500" : ""}`}
              >
                {item.name}
              </div>
              <div className="text-xs text-gray-500">{item.subtitle}</div>
              <ReplyMuteBadge mute={item.replyMute} locale={locale} />
            </div>
            {item.lastMessageAt && (
              <div className="flex-shrink-0 text-xs text-gray-400">
                {formatTimestamp(item.lastMessageAt, locale)}
              </div>
            )}
          </div>
        </div>
      )}
    />
  );
}
